import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  delay,
  filter,
  map,
  startWith,
  switchMap,
  take,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  AdminSaleCourseCancelMode,
  Sale,
  SaleUpdateDto,
  StatusCode
} from '../sale'
import { saleSelectors } from '../sale.selectors'
import { saleActions } from '../sale.actions'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Actions, ofType } from '@ngrx/effects'
import { NotificationService } from 'src/app/core/notification.service'
import { saleItemSelectors } from '../sale-item/sale-item.selectors'
import { CancellationPolicy } from 'src/app/shared/interfaces'
import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { environment } from 'src/environments/environment'
import { saleItemActions } from '../sale-item/sale-item.actions'
import { paymentActions } from '../payment/payment.actions'
import { paymentSelectors } from '../payment/payment.selectors'
import { userRoleActions } from '../../role/user-role/user-role.actions'
import { BookingTableComponent } from '../../booking/booking-table/table.component'
import { Router } from '@angular/router'
import { userRoleSelectors } from '../../role/user-role/user-role.selectors'
import { RolesType } from '../../role/role'
import { answerActions } from '../../survey/answer/answer.actions'
import { Answer } from '../../survey/answer/answer'
import { answerSelectors } from '../../survey/answer/answer.selectors'
import { documentActions } from '../../document/document.actions'
import { productActions } from '../../product/product.actions'
import { BsModalService } from 'ngx-bootstrap/modal'
import { CancelPolicyModalComponent } from 'src/app/shared/components/cancel-policy-modal/show.component'
import { operationActions } from '../../operation/operation.actions'
import { operationSelectors } from '../../operation/operation.selectors'
import { OperationShowModalComponent } from '../../operation/show-modal/show.component'
import { TranslateService } from '@ngx-translate/core'
import { SaleItem } from '../sale-item/sale-item'
import { SaleCourseCancelModalComponent } from '../sale-course-cancel-modal/sale-course-cancel-modal.component'
import { activityActions } from '../../activity/activity.actions'
import { ActivityTypes } from '../../activity/activity'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
@UntilDestroy()
@Component({
  selector: 'sale-show',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleShowComponent implements OnInit {
  public sale$: Observable<Sale>
  public saleId$: Observable<number>
  public isLoading$: Observable<boolean>
  public saleStatusCode = StatusCode
  public saleContainsSubscription: boolean = false
  public cancellationPolicies$: Observable<CancellationPolicy[]>
  public saleModuleConfig = environment.moduleConfigs.saleModule
  public saleIsAdjustable: boolean = true
  public moduleConfigs = environment.moduleConfigs
  public accessRights = AccessRights
  public answers$: Observable<Answer[]>
  public multidayEnabled$: Observable<boolean>
  public operationsPending$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false)
  constructor(
    private store: Store<AppState>,
    private router: Router,
    private notificationService: NotificationService,
    private actions$: Actions,
    private modalService: BsModalService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.store.dispatch(userRoleActions.resetSearched())

    this.isLoading$ = this.store
      .select(saleSelectors.selectIsLoading)
      .pipe(startWith(false))

    this.cancellationPolicies$ = this.store
      .select(configurationOptionSelectors.selectByKey, 'cancellation_policies')
      .pipe(
        filter((x) => Boolean(x)),
        map((configOption) => JSON.parse(configOption.value))
      )
    this.multidayEnabled$ = this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<boolean>(
          ConfigOptionsKeys.MULTIDAY_ENABLED
        )
      )
      .pipe(map(Boolean))
    this.saleId$ = this.store
      .select(saleSelectors.selectIdRouteParam)
      .pipe(filter((id) => Boolean(id)))
    this.saleId$.pipe(take(1)).subscribe((saleId) => {
      if (saleId) {
        this.fetchSale(saleId)

        this.actions$
          .pipe(
            ofType(saleItemActions.upsertMany),
            take(1),
            timeout(5000),
            catchError((error) => of(null))
          )
          .subscribe((action) => {
            if (action) {
              let totalProducts = []
              action.entities.map((saleItem) => {
                if (
                  saleItem.params?.bookableLinkedProductId &&
                  !totalProducts.includes(
                    saleItem.params?.bookableLinkedProductId
                  )
                ) {
                  totalProducts.push(saleItem.params?.bookableLinkedProductId)
                }
                if (
                  saleItem.params?.bookableProductId &&
                  !totalProducts.includes(saleItem.params?.bookableProductId)
                ) {
                  totalProducts.push(saleItem.params?.bookableProductId)
                }
              })
              if (totalProducts.length > 0) {
                this.store.dispatch(
                  productActions.fetchMany(
                    productActions.fetchMany({
                      params: RequestQueryBuilder.create({
                        search: {
                          id: { [CondOperator.IN]: totalProducts }
                        }
                      })
                    })
                  )
                )
              }
            }
          })
      }
      this.fetchSurveys(saleId)
    })
    this.sale$ = this.saleId$.pipe(
      filter((saleId) => Boolean(saleId)),
      switchMap((saleId) => this.store.select(saleSelectors.selectById, saleId))
    )

    combineLatest([
      this.sale$,
      this.store.select(saleItemSelectors.selectEntities),
      this.store.select(paymentSelectors.selectEntities)
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([sale, saleItemEntities, paymentEntities]) => {
        if (sale?.payments?.length) {
          this.saleIsAdjustable = false
          let isUnpaidPayment = false
          let salePaymentsTotalAmount = 0
          sale?.payments?.forEach((paymentId) => {
            salePaymentsTotalAmount += Number(
              paymentEntities[paymentId]?.amount
            )

            if (paymentEntities[paymentId]?.collectStatus != 0) {
              isUnpaidPayment = true
            }
          })

          if (
            salePaymentsTotalAmount < Number(sale?.total) ||
            isUnpaidPayment
          ) {
            this.saleIsAdjustable = true
          }
        }

        sale?.saleItems?.forEach((saleItemId) => {
          if (saleItemEntities[saleItemId]?.subscriptionId) {
            this.saleContainsSubscription = true
            return
          }
        })
      })

    this.saleId$.pipe(take(1)).subscribe((saleId) => {
      if (saleId) {
        //clean url necessary when redirect from diferents modals
        this.router.navigate(
          [
            {
              outlets: {
                primary: `sales/${saleId}`,
                modal: null
              }
            }
          ],
          { replaceUrl: true }
        )
      }

      this.store.dispatch(
        BreadCrumbActions.initHistory({
          pageName: 'saleModule.sales',
          url: `/sales`,
          titleName: ['general.general'],
          resourceType: 'sales',
          entityId: Number(saleId)
        })
      )
      this.store.dispatch(
        operationActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              $and: [
                { saleId: saleId },
                { isPending: true },
                { type: 'approve-participations-by-sale' }
              ]
            }
          })
        })
      )

      this.actions$
        .pipe(ofType(operationActions.upsertMany))
        .subscribe((action) => {
          this.operationsPending$.next(
            action.entities.filter(
              (opeartions) =>
                opeartions.type == 'approve-participations-by-sale'
            ).length > 0
          )
        })
    })
    this.fetchDocuments()
  }
  public fetchDocuments() {
    this.store.dispatch(
      documentActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { byDefault: true }
        })
      })
    )
  }
  public onClickCancel(policyId?: string) {
    this.store
      .select(saleItemSelectors.selectAll)
      .pipe(
        take(1),
        withLatestFrom(this.store.select(saleSelectors.selectIdRouteParam))
      )
      .subscribe(([saleItems, saleId]) => {
        const saleItemsForSale = saleItems.filter(
          (saleItem) => saleItem.saleId == saleId
        )

        if (this.saleContainsCourse(saleItemsForSale)) {
          this.openCourseCancelBehaviorModal(saleId, saleItemsForSale, policyId)
          return
        }

        const message = this.translate.instant('saleModule.cancelSale')
        if (!confirm(message)) {
          return
        }

        this.cancelSale(
          saleId,
          saleItemsForSale,
          this.buildCancelParams(policyId)
        )
      })
  }
  public subscribeToEmitter(componentRef): void {
    if (!(componentRef instanceof BookingTableComponent)) {
      return
    }
    const bookingTable: BookingTableComponent = componentRef
    bookingTable.onClickRow
      .pipe(untilDestroyed(this), withLatestFrom(this.saleId$))
      .subscribe(([item, saleId]) => {
        this.router.navigateByUrl(`/bookings/${item.id}`, {
          state: { returnUrl: `/sales/${saleId}` }
        })
      })
  }

  public hasRequiredRole$(roles: RolesType[]): Observable<boolean> {
    if (!roles?.length) {
      return of(true)
    }
    return this.store.select(userRoleSelectors.selectUserHasRoles, roles)
  }
  private fetchSurveys(saleId) {
    this.store.dispatch(
      answerActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { saleId: { [CondOperator.IN]: [saleId] } },
          limit: 1
        })
      })
    )
    this.answers$ = this.store
      .select(answerSelectors.selectAll)
      .pipe(
        map((answers) => answers.filter((answer) => answer.saleId == saleId))
      )
  }
  public approveOperationSale() {
    this.saleId$.pipe(take(1)).subscribe((saleId) => {
      this.store
        .select(operationSelectors.selectByFilter, {
          field: 'saleId',
          value: saleId
        })
        .pipe(take(1))
        .subscribe((operation) => {
          if (operation[0]?.id) {
            this.modalService.show(OperationShowModalComponent, {
              initialState: {
                operationId: operation[0].id
              },
              class: 'modal-lg'
            })
          }
        })
      this.actions$
        .pipe(ofType(operationActions.upsertMany))
        .subscribe((action) => {
          if (action.entities[0].saleId == saleId) {
            window.location.reload()
          }
        })
    })
  }
  public openCancelPolicyModal() {
    this.saleId$.pipe(take(1)).subscribe((saleId) => {
      const modalRef = this.modalService.show(CancelPolicyModalComponent, {
        initialState: {
          cancellationPolicies$: this.cancellationPolicies$,
          saleId: saleId
        },
        class: 'modal-lg'
      })

      modalRef.content.updateSuccess
        .pipe(take(1), untilDestroyed(this))
        .subscribe(() => this.refreshSalePayments(saleId))
    })
  }

  private fetchSale(saleId: number) {
    this.store
      .select(paymentSelectors.selectByFilter, {
        field: 'saleId',
        value: saleId
      })
      .pipe(take(1))
      .subscribe((payments) => {
        if (payments.length) {
          this.store.dispatch(
            paymentActions.removeMany({
              ids: payments.map((payment) => payment.id)
            })
          )
        }

        this.store.dispatch(
          saleActions.fetchOneById({
            id: saleId,
            params: RequestQueryBuilder.create({
              join: [
                { field: 'creditSales' },
                { field: 'saleItems' },
                { field: 'saleItems.booking' },
                { field: 'saleItems.booking.linkedProduct' },
                { field: 'saleItems.booking.product' },
                { field: 'saleItems.product' },
                { field: 'saleItems.subscription' },
                { field: 'saleItems.course' },
                { field: 'saleItems.course.product' },
                { field: 'saleItems.bookingItems' },
                { field: 'customer' },
                { field: 'customer.organization' },
                { field: 'payments' },
                { field: 'documents' }
              ]
            })
          })
        )
      })
  }

  private saleContainsCourse(saleItems: SaleItem[]): boolean {
    return saleItems.some((saleItem) => Boolean(saleItem.courseId))
  }

  private openCourseCancelBehaviorModal(
    saleId: number,
    saleItems: SaleItem[],
    policyId?: string
  ): void {
    const modalRef = this.modalService.show(SaleCourseCancelModalComponent, {
      class: 'modal-sm modal-dialog-centered modal-dialog-scrollable',
      backdrop: 'static'
    })

    modalRef.onHide.pipe(take(1), untilDestroyed(this)).subscribe(() => {
      const courseCancelMode = modalRef.content
        ?.selectedMode as AdminSaleCourseCancelMode | null

      if (!courseCancelMode) {
        return
      }

      this.cancelSale(
        saleId,
        saleItems,
        this.buildCancelParams(policyId, courseCancelMode)
      )
    })
  }

  private buildCancelParams(
    policyId?: string,
    courseCancelMode?: AdminSaleCourseCancelMode
  ): SaleUpdateDto {
    return {
      statusCode: StatusCode.CANCELLED,
      ...(policyId ? { cancelationPolicyUuid: policyId } : {}),
      ...(courseCancelMode ? { courseCancelMode } : {})
    }
  }

  private cancelSale(
    saleId: number,
    saleItems: SaleItem[],
    params: SaleUpdateDto
  ): void {
    this.store.dispatch(
      saleItemActions.removeMany({
        ids: saleItems.map((saleItem) => saleItem.id)
      })
    )

    this.store.dispatch(
      saleActions.updateOneById({
        id: saleId,
        params
      })
    )

    this.actions$
      .pipe(
        ofType(saleActions.upsertMany),
        filter((action) =>
          action.entities.some((sale) => Number(sale.id) === Number(saleId))
        ),
        take(1),
        delay(0),
        untilDestroyed(this),
        timeout(5000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          const sale: Sale = action?.entities.find(
            (sale) => Number(sale.id) === Number(saleId)
          )
          this.refreshSalePayments(saleId)
          if (sale?.creditSales) {
            this.notificationService.emit('alerts.success', 'success')
          }
          this.store.dispatch(
            activityActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: {
                  typeId: {
                    [CondOperator.IN]: [
                      ActivityTypes.SALE_ITEM_REFUNDED,
                      ActivityTypes.SALE_CANCELLED
                    ]
                  },
                  saleId: saleId
                }
              })
            })
          )
        }
      })
  }

  private refreshSalePayments(saleId: number): void {
    this.store
      .select(paymentSelectors.selectByFilter, {
        field: 'saleId',
        value: saleId
      })
      .pipe(take(1))
      .subscribe((payments) => {
        if (payments.length) {
          this.store.dispatch(
            paymentActions.removeMany({
              ids: payments.map((payment) => payment.id)
            })
          )
        }

        this.store.dispatch(
          paymentActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                saleId
              }
            })
          })
        )
      })
  }
}
